import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { priorityColors } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { useUpcomingTasksChartQuery } from '@/modules/admin-dashboard/queries/tasks.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  Calendar,
  CalendarMode,
  InfoCard,
  Select,
  SelectButtonsNew,
  Skeleton,
  StylesConfig,
} from 'ui';
import { TaskChartItem, TaskListMode, TaskPriority } from 'compliance-api';

const monthsOptions = [
  { label: 'January', value: 0 },
  { label: 'February', value: 1 },
  { label: 'March', value: 2 },
  { label: 'April', value: 3 },
  { label: 'May', value: 4 },
  { label: 'June', value: 5 },
  { label: 'July', value: 6 },
  { label: 'August', value: 7 },
  { label: 'September', value: 8 },
  { label: 'October', value: 9 },
  { label: 'November', value: 10 },
  { label: 'December', value: 11 },
];

const modeOptions = [
  { label: 'Month', value: 'month' as CalendarMode },
  { label: 'Year', value: 'year' as CalendarMode },
];

// todo: navigation (do not navigate to date without tasks)
const getYearsOptions = () => {
  const startYear = dayjs().year() - 10;
  const res = [];

  for (let i = 0; i < 20; i++) {
    res.push({ label: (startYear + i).toString(), value: startYear + i });
  }
  return res;
};

const getDefaultDateSettings = () => {
  return {
    month: dayjs().month(),
    year: dayjs().year(),
  };
};

interface UpcomingTaskCalendarFilters {
  mode: CalendarMode;
  dateSettings: {
    month: number;
    year: number;
  };
}

const selectsStyles: StylesConfig = {
  menu: provider => ({ ...provider, zIndex: 3 }),
  dropdownIndicator: provider => ({ ...provider, padding: '6px 8px 6px' }),
  container: provider => ({ ...provider, minWidth: 125 }),
  control: provider => ({
    ...provider,
    padding: '0 0 0 8px',
    border: '1px solid rgba(23, 23, 23, 0.12)',
    minHeight: 32,
  }),
};

const UpcomingTaskCalendarV2 = () => {
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<UpcomingTaskCalendarFilters>({
    mode: 'month',
    dateSettings: getDefaultDateSettings(),
  });

  useEffect(() => {
    setFilters({
      mode: 'month',
      dateSettings: getDefaultDateSettings(),
    });
  }, [businessId]);
  const { mode, dateSettings } = filters;
  const navigate = useNavigate();

  const calendarValue = useMemo(
    () => dayjs().month(dateSettings.month).year(dateSettings.year),
    [dateSettings.month, dateSettings.year],
  );

  const datesFilter = useMemo(() => {
    return {
      from_date: calendarValue.startOf(mode).format('YYYY-MM-DD'),
      to_date: calendarValue.endOf(mode).format('YYYY-MM-DD'),
    };
  }, [calendarValue, mode]);

  const { data, isFetching } = useUpcomingTasksChartQuery(datesFilter);

  const callRender = useCallback(
    (date: Dayjs) => {
      let tasks: TaskChartItem[];

      if (mode === 'month') {
        tasks = (data?.dates || {})[date.format('YYYY-MM-DD')] || [];
      } else {
        tasks = Object.entries(data?.dates || {}).reduce((acc, [dateX, tasks]) => {
          return dateX.startsWith(
            `${date.year()}-${(date.month() + 1).toString().padStart(2, '0')}`,
          )
            ? [...acc, ...tasks]
            : acc;
        }, [] as TaskChartItem[]);
      }

      if ((!tasks || !tasks.length) && !isFetching) return null;

      const tasksToRender = tasks.slice(0, 3);
      const extraTasksLength = tasks.length - tasksToRender.length;

      return (
        <div className="text-xs">
          {isFetching && (
            <div>
              <Skeleton height={10} />
            </div>
          )}
          {!isFetching &&
            tasksToRender.map(({ id, title, priority }, index) => (
              <div
                key={`${mode}-${date.format(
                  mode === 'month' ? 'YYYY-MM-DD' : 'YYYY-MM',
                )}-${id}-${index}`}
                className="flex gap-2 items-center min-h-3"
              >
                <div
                  className={classNames(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    priorityColors[priority],
                  )}
                />
                <div className="truncate hidden md:block">{title}</div>
              </div>
            ))}
          {!isFetching && !!extraTasksLength && (
            <div className="text-gray-500 hidden md:block">and {extraTasksLength} more</div>
          )}
        </div>
      );
    },
    [data?.dates, isFetching, mode],
  );

  const headerRender = useCallback(
    (config: {
      value: Dayjs;
      type: CalendarMode;
      onChange: (date: Dayjs) => void;
      onTypeChange: (type: CalendarMode) => void;
    }) => {
      return (
        <div>
          <div className="flex justify-between mb-2.5">
            <div className="text-base font-medium">Upcoming Task Calendar</div>
            <div className="flex gap-2 flex-wrap justify-end">
              {mode === 'month' && (
                <Select
                  selectStyles={selectsStyles}
                  options={monthsOptions}
                  value={config.value.month()}
                  onChange={month => {
                    if (month === null) return;
                    config.onChange(config.value.set('month', month));
                  }}
                />
              )}

              <Select
                selectStyles={selectsStyles}
                options={getYearsOptions()}
                value={config.value.year()}
                onChange={year => {
                  if (year === null) return;
                  config.onChange(config.value.set('year', year));
                }}
              />

              <SelectButtonsNew
                classes={{ container: 'min-w-[150px]' }}
                value={config.type}
                options={modeOptions}
                onChange={mode => setFilters(filters => ({ ...filters, mode }))}
              />
            </div>
          </div>
          <div className="flex gap-2">
            {Object.values(TaskPriority).map(priority => (
              <div key={priority} className="flex gap-2 items-center mb-1">
                <div
                  className={classNames(
                    'w-2.5 h-2.5 rounded-full shrink-0',
                    priorityColors[priority],
                  )}
                />
                <div className="capitalize">{priority}</div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [mode, setFilters],
  );

  const handleChange = useCallback(
    (date: Dayjs) => {
      setFilters(filters => ({
        ...filters,
        dateSettings: {
          month: date.month(),
          year: date.year(),
        },
      }));
    },
    [setFilters],
  );

  const handleSelect = useCallback(
    (date: Dayjs, { source }: { source: 'month' | 'year' | 'date' | 'customize' }) => {
      if (!['month', 'date'].includes(source)) return;

      navigate(
        {
          pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.AllTasks),
          search: location.search,
        },
        {
          state: {
            from_date:
              source === 'date'
                ? date.format('YYYY-MM-DD')
                : date.startOf('month').format('YYYY-MM-DD'),
            to_date:
              source === 'date'
                ? date.format('YYYY-MM-DD')
                : date.endOf('month').format('YYYY-MM-DD'),
          },
        },
      );
    },
    [navigate],
  );
  return (
    <InfoCard
      classes={{
        content: classNames({
          '[&_[class*="ant-picker-cell-selected"]>div]:!bg-white [&_[class*="ant-picker-cell-selected"]>div>div]:!text-[rgba(0,_0,_0,_0.88)]':
            dateSettings.month !== dayjs().month() || dateSettings.year !== dayjs().year(),
        }),
      }}
    >
      <Calendar
        mode={mode}
        value={calendarValue}
        cellRender={callRender}
        onChange={handleChange}
        headerRender={headerRender}
        onSelect={handleSelect}
        className="[&_thead_th]:text-left [&_thead_th]:!pl-3 [&_thead_th]:!pb-2 [&_thead_th]:!text-gray-500"
      />
    </InfoCard>
  );
};

export default UpcomingTaskCalendarV2;
